import React from 'react';

const features = [
  {
    title: 'Quote with real-world pricing',
    body: 'On Pro, search materials by city and region, add labor, tax, overhead, and markup, then generate a professional PDF in minutes.',
    proOnly: true,
    icon: (
      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.414L11 9.586V6z"
          clipRule="evenodd"
        />
      </svg>
    ),
    tone: 'primary',
  },
  {
    title: 'Keep every client organized',
    body: 'Store client details, project history, quote activity, contact information, and job notes without digging through old messages.',
    icon: (
      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zm12 6H4v8a2 2 0 002 2h8a2 2 0 002-2v-8zM8 9a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1zm4 0a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
    tone: 'accent',
  },
  {
    title: 'Manage projects from quote to invoice',
    body: 'Turn accepted quotes into organized projects with costs, files, receipts, workers, and job details attached.',
    proOnly: true,
    icon: (
      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    ),
    tone: 'primary',
  },
  {
    title: 'Know your job costs',
    body: 'Track materials, labor, receipts, expenses, and estimated vs. actual costs so every project stays easier to control.',
    proOnly: true,
    icon: (
      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
          clipRule="evenodd"
        />
      </svg>
    ),
    tone: 'accent',
  },
  {
    title: 'Look more professional',
    body: 'Create branded quotes and invoices with your company logo and clean layouts. Email PDFs directly from Devello on Pro.',
    icon: (
      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
      </svg>
    ),
    tone: 'primary',
  },
  {
    title: 'Team member portal on Pro',
    body: 'Invite up to two team members with their own login. Control project access and permissions so field staff can upload photos, notes, and expenses safely.',
    proOnly: true,
    icon: (
      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    tone: 'accent',
  },
];

const FeaturesSection = () => {
  return (
    <section id="why-contractors" className="bg-slate-50/50 py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 text-center md:mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Why DevelloTech</p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            Everything scattered across notes, spreadsheets, texts, and receipts? Put it in one place.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600 md:text-lg">
            The all-in-one dashboard for contractors who need organized quotes, projects, files, and job costs from first estimate to final invoice.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-7 lg:grid-cols-3">
          {features.map((f) => (
            <div
              key={f.title}
              className="feature-card rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm"
            >
              <div
                className={`mb-6 flex h-12 w-12 items-center justify-center rounded-2xl ${
                  f.tone === 'accent' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'
                }`}
              >
                {f.icon}
              </div>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-bold text-gray-900">{f.title}</h3>
                {f.proOnly && (
                  <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    Pro
                  </span>
                )}
              </div>
              <p className="text-sm leading-relaxed text-slate-600 md:text-base">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
